import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { client, queryStringifyObject } from 'shared';
import { PageHeader, Row, Select, Stack } from 'library/ui';
import { useUserContext } from 'UserContext';

import StaffSendJob from '../ProviderSearch/StaffSendJob';
import { WORKED } from './constants';
import { Filters, Search, Results, HiringPolicyNote } from './components';

import type { PagingResult } from 'models';
import type { FilterProps, IProviderSearch } from './models';

const SORT_OPTIONS = [
    { value: 'Most recently worked', key: 'last_shift' },
    { value: 'Name (A–Z)', key: 'name_asc' },
    { value: 'Name (Z–A)', key: 'name_desc' },
];

export function ProvidersSearchAndFilter() {
    const { user } = useUserContext();
    const location = useLocation();

    const businessId = user?.businesses_active?.[0]?.id;
    const startOnSubmittedFilter = new URLSearchParams(location.search).get('clearinghouse') === 'submitted';

    const initialPageResults: PagingResult<IProviderSearch> = {
        count: 0,
        per_page: 0,
        num_pages: 0,
        results: [],
    };

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [pageResult, setPageResult] = useState<PagingResult<IProviderSearch>>(initialPageResults);
    const [selectedProviders, setSelectedProviders] = useState<IProviderSearch[]>([]);
    const [formValues, setFormValues] = useState<FilterProps>({
        page: 1,
        searchQuery: '',
        workerType: WORKED,
        showPreferred: false,
        pendingClearinghouse: startOnSubmittedFilter,
        address: '-1',
        sort: 'name_asc',
    });
    const timeoutRef = useRef<number | null>(null);

    const getProviders = (params: string) =>
        client(`api/providers/?${params}`)
            .then((data: PagingResult<IProviderSearch>) => {
                setError(false);
                formValues.page > 1
                    ? setPageResult({ ...data, results: [...pageResult.results, ...data.results] })
                    : setPageResult(data);
            })
            .catch(() => setError(true))
            .finally(() => {
                setLoading(false);
            });

    function handleChange(value: string | boolean, formKey: string) {
        setLoading(true);

        if (formKey === 'page') {
            setFormValues({ ...formValues, page: formValues.page + 1 });
        } else {
            setFormValues({ ...formValues, page: 1, [formKey]: value });
        }
    }

    function refresh() {
        setLoading(true);
        setFormValues({ ...formValues, page: 1 });
    }

    function handleSelect(checked: boolean, provider: IProviderSearch) {
        setSelectedProviders(
            selectedProviders.includes(provider) && !checked
                ? selectedProviders.filter((p) => p.id !== provider.id)
                : [...selectedProviders, provider],
        );
    }

    useEffect(() => {
        const searchParams = formValues?.searchQuery?.length ? { text: formValues.searchQuery } : {};

        const params = queryStringifyObject({
            ...searchParams,
            myproviders: formValues.workerType,
            page: formValues.page,
            page_size: 12,
            show_preferred_workers: formValues.showPreferred,
            pending_clearinghouse: formValues.pendingClearinghouse,
            address: formValues.address !== '-1' ? formValues.address : null,
            sort: formValues.sort,
            business_id: businessId,
        });

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = window.setTimeout(() => getProviders(params), 500);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues]);

    return (
        <Stack maxWidth="lg" gap="md">
            <PageHeader title="Workers" subtitle="Browse workers who have worked or are scheduled at your center" />
            <Row gap="sm">
                <Search value={formValues.searchQuery ?? ''} handleChange={handleChange} />
                <Select
                    value={formValues.sort}
                    options={SORT_OPTIONS}
                    onChange={(value) => handleChange(value, 'sort')}
                />
                <Filters formValues={formValues} handleChange={handleChange} loading={loading} />
            </Row>
            <HiringPolicyNote />
            <StaffSendJob
                selectedProviders={selectedProviders}
                resetSelectedProviders={() => setSelectedProviders([])}
            />
            <Results
                loading={loading}
                error={error}
                providers={pageResult.results}
                selectedProviders={selectedProviders}
                handleSelect={handleSelect}
                loadMore={formValues.page < pageResult.num_pages}
                handleChange={handleChange}
                onClearinghouseUpdated={refresh}
            />
        </Stack>
    );
}
